import { CSSProperties, useMemo, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import { IconStar } from "@tabler/icons-react";
import { classNames, momentizeDate } from "@ct-react/core";
import { ReviewsData, ReviewsRates } from "@shared/graphql";
import { DataProps } from "../../tools/components";
import { Wrapper } from "../layout/wrapper";
import ReviewCard from "../review-card";
import ReviewsIntoModal, { ReviewsHandler } from "./reviews-into-modal";
import "./reviews.scss";

const transDefs = defineMessages({
  title: { id: "article.rubrics.reviews.title", defaultMessage: "Évaluations" },
  average: { id: "article.rubrics.reviews.average", defaultMessage: "{rate} ({count, plural, =1 {# avis} other {# avis}})" },
});

const ratesDefs = defineMessages<keyof ReviewsRates>({
  welcome: { id: "article.rubrics.reviews.welcome-rate", defaultMessage: "Accueil" },
  reservation: { id: "article.rubrics.reviews.reservation-rate", defaultMessage: "Réservation" },
  overallComfort: { id: "article.rubrics.reviews.overall-rate", defaultMessage: "Confort général" },
  bedsComfort: { id: "article.rubrics.reviews.beds-rate", defaultMessage: "Confort des lits" },
  cleanliness: { id: "article.rubrics.reviews.cleanliness-rate", defaultMessage: "Propreté" },
  accuracy: { id: "article.rubrics.reviews.accuracy-rate", defaultMessage: "Précision" },
  location: { id: "article.rubrics.reviews.location-rate", defaultMessage: "Lieu" },
  satisfaction: { id: "article.rubrics.reviews.satisfaction-rate", defaultMessage: "Satisfaction" },
  value: { id: "article.rubrics.reviews.value-rate", defaultMessage: "Qualité-prix" },
});

const ReviewsPart = ({ className, data  }: DataProps<ReviewsData>) => {

  const intl = useIntl();
  const wrapperClasses = classNames("reviews-part", className);

  const modalRef = useRef<ReviewsHandler>(null);

  const cleanRates = useMemo(() => {
    const { __typename, ...result } = data.rates as any;
    return result as ReviewsRates;
  }, [ data ]);

  return (
    <div className={wrapperClasses}>

      <Wrapper.Centered as="section" className="content">
        <h2>
          {intl.formatMessage(transDefs.title)}
          <span className="average">
            <IconStar className="s-icon" />
            {intl.formatMessage(transDefs.average, {
              rate: intl.formatNumber(data.averageRate, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
              count: data.ratesCount
            })}
          </span>
        </h2>
        <div className="ratings-list">
          {Object.entries(cleanRates).map(([ key, rate ]) => (
            <div key={key} className="rate-item">
              <span className="label">{intl.formatMessage(ratesDefs[key as keyof ReviewsRates])}</span>
              <div className="value">
                <div className="bar">
                  <span style={{ "--bar-rating": rate } as CSSProperties} />
                </div>
                <span>{intl.formatNumber(rate, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
              </div>
            </div>)
          )}
        </div>
      </Wrapper.Centered>

      {(!!data.comments && data.comments.length > 0) &&
        <>
          <Wrapper.ScrollableList className="content">
            <Wrapper.ScrollableListHeader />
            <Wrapper.ScrollableListContent>
              {data.comments.map((item, index) => {
                const { reviewedAt, ...data } = item;
                return (
                  <ReviewCard key={index}
                              loading={false} data={{... data, reviewedAt: momentizeDate(reviewedAt) }}
                              onMore={() => modalRef.current?.display(index)} />);
                }
              )}
            </Wrapper.ScrollableListContent>
          </Wrapper.ScrollableList>
          <ReviewsIntoModal ref={modalRef} data={data.comments} />
        </>
      }

    </div>);

};

export default ReviewsPart;
